import { styled } from '@mui/material/styles';
import {
  IconButton,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { NEW_COLORS } from '@hapvida/hapvida-core-components/src/themes/colors/newColors';

import type { RowWrapperProps } from './types';

const getBorderColor = (open: boolean) =>
  open ? NEW_COLORS.SEMANTICS.WARNING[400] : NEW_COLORS.GRAYSCALE[100];

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'open',
})<RowWrapperProps>(() => ({
  position: 'relative',
  '& th': {
    borderBottom: 'unset',
  },
}));

const StyledTableHead = styled(TableHead)(() => ({
  '& th': {
    borderBottom: 'unset',
  },
}));

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: prop =>
    prop !== 'isFirst' && prop !== 'isLast' && prop !== 'open',
})<{ isFirst: boolean; isLast: boolean; open: boolean }>(
  ({ isFirst, open }) => ({
    transition: 'border-bottom 300ms ease',
    borderTop: `1px solid ${getBorderColor(open)}`,
    borderBottom: `1px solid ${NEW_COLORS.GRAYSCALE[100]}`,
    borderLeft: isFirst ? `1px solid ${getBorderColor(open)}` : 'none',
    borderRight: 'none',
    borderTopLeftRadius: isFirst ? '8px' : 0,
    borderBottomLeftRadius: !open && isFirst ? '8px' : 0,
    backgroundColor: open ? NEW_COLORS.SECONDARY[25] : 'transparent',
  }),
);

const StyledThreeLineText = styled(Typography)({
  fontSize: '12px',
  lineHeight: 1.2,
  color: NEW_COLORS.GRAYSCALE[800],
});

const StyledSingleLineText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isBold',
})<{ isBold?: boolean }>(({ isBold }) => ({
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '200px',
  fontWeight: isBold ? 700 : 400,
}));

const StyledSimpleText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isBold',
})<{ isBold?: boolean }>(({ isBold }) => ({
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  fontWeight: isBold ? 900 : 400,
}));

const StyledActionCell = styled(TableCell)<{ open: boolean }>(({ open }) => ({
  position: 'sticky',
  right: 0,
  zIndex: 0,
  backgroundColor: open ? NEW_COLORS.SECONDARY[50] : NEW_COLORS.GRAYSCALE[50],
  borderTop: `1px solid ${getBorderColor(open)}`,
  borderLeft: 'none',
  borderRight: `1px solid ${getBorderColor(open)}`,
  borderTopRightRadius: 8,
  borderBottomRightRadius: !open ? 8 : 0,
  padding: 0,
  width: '56px',
  borderBottom: `1px solid ${NEW_COLORS.GRAYSCALE[100]}`,
}));

const StyledIconButton = styled(IconButton)({
  padding: 0,
  width: '56px',
  height: '56px',
});

export {
  StyledIconButton,
  StyledActionCell,
  StyledSingleLineText,
  StyledThreeLineText,
  StyledSimpleText,
  StyledTableCell,
  StyledTableRow,
  StyledTableHead,
};
